import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { api } from './api.js';

interface AuthContextValue {
  isAuthenticated: boolean;
  login: (password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!api.getToken());

  const login = useCallback(async (password: string) => {
    api.setToken(password);
    // Test the token by hitting the settings endpoint
    try {
      await api.get('/settings');
      setIsAuthenticated(true);
    } catch {
      api.clearToken();
      throw new Error('Invalid password');
    }
  }, []);

  const logout = useCallback(() => {
    api.clearToken();
    setIsAuthenticated(false);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
